import { useEffect, useState } from 'react'
import { api, type Agreement as Measured, type OverrideCause, type VerdictState } from '../api'
import { ANSWER_LABEL, CAUSE_LABEL, OUTCOME_BADGE, OUTCOME_LABEL, agreementPercent } from '../agreementWords'
import { AddOnFootnote, EvaluatorName } from '../components/AddOn'
import { RowMenu } from '../components/RowMenu'
import { DeskCell, DeskHeader, useDesks } from '../desks'
import { sortRows, useSort } from '../components/Grid'

/**
 * Compliance agreement — proposal §6 second-release gate, §7; IMPLEMENTATION.md decisions 52 and 53.
 * The Snapshot Evaluator pack, under Dashboards.
 *
 * How compliance's reviews of GUARD's verdicts came out. **Summary** opens on tiles: the
 * agreement, then how many verdicts were reviewed, agreed, overridden and still await review,
 * and how many funds have a day reviewed in full. Each fund follows, with a way to its
 * verdicts. A limit written as words is never judged by GUARD, so compliance's reading of it
 * counts as reviewed and sits in its own column rather than in the agreement.
 * **Overridden** lists each verdict compliance overrode, or decided where GUARD could not, with
 * both verdicts, the cause and the note. Reviewing itself happens on a verdict, from its menu,
 * where GUARD's figures are in front of the reviewer.
 */

type Part = 'summary' | 'overridden'

const CAUSES: OverrideCause[] = ['limit', 'data', 'reading', 'accepted']

const STATE_LABEL: Record<VerdictState, string> = {
  breach: 'Breach',
  not_evaluable: 'Not evaluable',
  warn: 'Warn',
  pass: 'Pass',
}

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function Tile({ label, value, note, figure }: { label: string; value: string | number; note?: string; figure?: boolean }) {
  return (
    <div className={`snapshot-tile${figure ? ' agreement-figure-tile' : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {note && <small>{note}</small>}
    </div>
  )
}

export function Agreement({ onReview }: { onReview?: (runId: string) => void }) {
  const [data, setData] = useState<Measured | null>(null)
  const [part, setPart] = useState<Part>('summary')
  const [error, setError] = useState<string | null>(null)
  /** The overrides' Desk column, for somebody across the whole firm (decision 91). */
  const [overrideDesk, setOverrideDesk] = useState('')
  const overrideSort = useSort()
  const directory = useDesks()

  useEffect(() => {
    api
      .agreement()
      .then(setData)
      .catch((e: unknown) => setError(said(e)))
  }, [])

  const overridden = data?.overrides.length ?? 0

  return (
    <section className="agreement">
      <header className="page-head">
        <h2>Compliance agreement</h2>
        <p className="note">
          How compliance’s reviews of GUARD’s verdicts came out: what it agreed, what it overrode and why, and what
          awaits review. Review a verdict from its menu among the fund’s verdicts. Part of the{' '}
          <EvaluatorName strong={false} />.
          <AddOnFootnote />
        </p>
      </header>

      <div className="tabstrip" role="tablist" aria-label="Compliance agreement">
        <button type="button" role="tab" aria-selected={part === 'summary'} onClick={() => setPart('summary')}>
          Summary
        </button>
        <button type="button" role="tab" aria-selected={part === 'overridden'} onClick={() => setPart('overridden')}>
          Overridden
          {overridden > 0 && <span className="count">{overridden}</span>}
        </button>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {data === null ? (
        !error && <p className="loading">Loading…</p>
      ) : part === 'summary' ? (
        <Summary data={data} onReview={onReview} />
      ) : (
        <>
          <div className="snapshot-tiles agreement-tiles" data-testid="agreement-causes">
            <Tile
              figure
              label="Overridden"
              value={data.counts.overridden}
              note={
                data.counts.decided_by_compliance
                  ? `and ${data.counts.decided_by_compliance} decided by compliance`
                  : undefined
              }
            />
            {CAUSES.map((c) => (
              <Tile key={c} label={CAUSE_LABEL[c]} value={data.causes[c] ?? 0} />
            ))}
          </div>
          {data.overrides.length === 0 ? (
            <p className="empty" data-testid="no-overrides">
              Compliance agrees with every verdict of GUARD’s it has reviewed.
            </p>
          ) : (
            <div className="table-scroll">
              <table className="grid stacked" data-testid="agreement-overrides">
                <thead>
                  <tr>
                    <DeskHeader
                      sort={overrideSort.sort}
                      onSort={overrideSort.onSort}
                      value={overrideDesk}
                      onChange={setOverrideDesk}
                    />
                    <th>Fund</th>
                    <th>Holdings as at</th>
                    <th>Limit</th>
                    <th>GUARD</th>
                    <th>Compliance</th>
                    <th>Why</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sortRows(
                    data.overrides.filter((o) => !overrideDesk || directory.fund(o.fund) === overrideDesk),
                    overrideSort.sort,
                    { desk: (o) => directory.fund(o.fund) ?? '' },
                  ).map((o) => (
                    <tr key={`${o.run_id}-${o.rule_id}`} data-testid="override">
                      <DeskCell fund={o.fund} />
                      <td>
                        <code>{o.fund}</code>
                      </td>
                      <td>{o.as_of}</td>
                      <td>
                        {o.limit}
                        <span className="clause">
                          <code>{o.rule_key}</code>
                        </span>
                      </td>
                      <td>
                        <span className={`verdict-count ${o.guard_state}`}>{STATE_LABEL[o.guard_state]}</span>
                      </td>
                      <td>
                        <span className={`verdict-count ${OUTCOME_BADGE[o.outcome]}`}>{ANSWER_LABEL[o.review.answer]}</span>
                      </td>
                      <td>
                        {o.review.cause ? CAUSE_LABEL[o.review.cause] : OUTCOME_LABEL[o.outcome]}
                        {o.review.note && <span className="clause">{o.review.note}</span>}
                        <span className="clause">
                          {o.review.reviewed_by ?? ''} · {new Date(o.review.reviewed_at).toLocaleString()}
                        </span>
                      </td>
                      <td className="run-actions">
                        {onReview && (
                          <RowMenu
                            label={`Actions for ${o.fund} ${o.limit}`}
                            items={[{ label: 'Open the verdicts', onSelect: () => onReview(o.run_id) }]}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function Summary({ data, onReview }: { data: Measured; onReview?: (runId: string) => void }) {
  /** Desk first, sortable and filterable, for somebody across the whole firm (decision 91). */
  const [desk, setDesk] = useState('')
  const { sort, onSort } = useSort()
  const desks = useDesks()
  const { counts, gate, headline } = data
  const read = counts.read_by_a_person
  const reviewed = counts.agreed + counts.overridden + counts.decided_by_compliance + read
  const awaiting = counts.awaiting_review + counts.changed_since_review

  return (
    <>
      <div className="snapshot-tiles agreement-tiles" data-testid="agreement-tiles">
        <Tile
          figure
          label="Agreement"
          value={headline ? agreementPercent(headline.ratio) : '—'}
          note={headline ? `${headline.agreed} of ${headline.compared} verdicts GUARD judged` : 'Nothing reviewed yet'}
        />
        <Tile
          label="Verdicts reviewed"
          value={reviewed}
          note={read ? `${read} written as words, read by compliance` : undefined}
        />
        <Tile label="Agreed" value={counts.agreed} />
        <Tile
          label="Overridden"
          value={counts.overridden}
          note={counts.decided_by_compliance ? `and ${counts.decided_by_compliance} decided by compliance` : undefined}
        />
        <Tile
          label="Awaiting review"
          value={awaiting}
          note={counts.changed_since_review ? `${counts.changed_since_review} changed since review` : undefined}
        />
        <Tile label="Funds fully reviewed" value={`${gate.reviewed} of ${gate.funds}`} note="every verdict of a day reviewed" />
      </div>
      {reviewed === 0 && (
        <p className="note" data-testid="agreement-empty">
          Review a fund’s verdicts from each verdict’s menu, and how often compliance agrees with GUARD is measured here.
        </p>
      )}

      {(data.desks ?? []).length > 1 && (
        <>
          <h3>Desks</h3>
          <div className="table-scroll">
            <table className="grid" data-testid="agreement-desks">
              <thead>
                <tr>
                  <th>Desk</th>
                  <th>Funds</th>
                  <th>Agreement</th>
                  <th>Agreed</th>
                  <th>Overridden</th>
                  <th>Awaiting</th>
                  <th>Every fund reviewed</th>
                </tr>
              </thead>
              <tbody>
                {data.desks!.map((d) => (
                  <tr key={d.desk}>
                    <td title={d.name}>{d.desk}</td>
                    <td className="limit">{d.funds}</td>
                    <td className="limit">{d.ratio === null ? '—' : agreementPercent(d.ratio)}</td>
                    <td className="limit">{d.agreed}</td>
                    <td className="limit">{d.overridden + d.decided}</td>
                    <td className="limit">{d.awaiting}</td>
                    <td>{d.gate_met ? 'Yes' : 'Not yet'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3>Funds</h3>
      <div className="table-scroll">
        <table className="grid" data-testid="agreement-funds">
          <thead>
            <tr>
              <DeskHeader sort={sort} onSort={onSort} value={desk} onChange={setDesk} />
              <th>Fund</th>
              <th>Latest holdings</th>
              <th>Verdicts</th>
              <th>Reviewed</th>
              <th>Awaiting review</th>
              <th>Agreed</th>
              <th>Overridden</th>
              <th>Decided by compliance</th>
              <th title="Limits written as words, which GUARD does not judge">Read by compliance</th>
              <th>Agreement</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sortRows(
              data.funds.filter((f) => !desk || desks.fund(f.portfolio_id) === desk),
              sort,
              { desk: (f) => desks.fund(f.portfolio_id) ?? '' },
            ).map((f) => (
              <tr key={f.portfolio_id} data-testid="agreement-fund">
                <DeskCell fund={f.portfolio_id} />
                <td>
                  <code>{f.code}</code>
                </td>
                <td>{f.standing_day ?? <span className="clause">Not evaluated</span>}</td>
                <td className="limit">{f.verdicts}</td>
                <td className="limit">{f.reviewed}</td>
                <td className="limit">{f.awaiting}</td>
                <td className="limit">{f.agreed}</td>
                <td className="limit">{f.overridden}</td>
                <td className="limit">{f.decided}</td>
                <td className="limit">{f.read_by_a_person}</td>
                <td className="limit">{f.ratio === null ? '—' : agreementPercent(f.ratio)}</td>
                <td className="run-actions">
                  {f.standing_run_id && onReview && (
                    <RowMenu
                      label={`Actions for ${f.code}`}
                      items={[{ label: 'Review verdicts', onSelect: () => onReview(f.standing_run_id!) }]}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
